import React, { forwardRef, useCallback, useEffect, useRef } from 'react';

import { useCombinedRefs } from '../hooks/useCombinedRefs';
import { useSlug } from '../hooks/useSlug';
import { Input } from '../Input';
import { InputLabel } from '../InputLabel';
import { classes, createId } from '../utils';

import { CheckboxProps as Props } from './types';

import styles from './styles.module.css';

export type { Props };

export const Checkbox = forwardRef<HTMLInputElement, Props>(
  function CheckboxComponent(props, ref) {
    const {
      checked,
      defaultChecked,
      id: userProvidedId,
      label,
      onChange,
      required,
      disabled,
      error,
      indeterminate = false,
      inputProps,
      name: userProvidedName,
      size = 'default',
      unstyled,
    } = props;

    const name = userProvidedName || 'checkbox-input';

    const [labelName, inputName] = useSlug(`${name}-label`, 'checkbox');
    const inputRef = useRef<HTMLInputElement | null>(null);
    const stableId = useRef(createId());
    const id =
      userProvidedId ||
      inputProps?.id ||
      userProvidedName ||
      label ||
      stableId.current;

    const combinedRef = useCombinedRefs(inputRef, ref);

    const wrapperClasses = classes(
      [
        {
          [styles.disabled]: disabled,
        },
      ],
      props
    );
    const labelClasses = classes([styles.label], {
      ...props,
      name: `${name}-label`,
    });

    const inputClasses = classes(
      [
        styles.input,
        styles[size],

        {
          [styles.error]: error,
        },
      ],
      {
        ...props,
        name: `${name}-input`,
      }
    );

    const localChange = useCallback(
      (ev: React.ChangeEvent<HTMLInputElement>) => {
        ev.persist();

        if (inputProps?.onChange) {
          inputProps.onChange(ev);
        }
        if (onChange) {
          onChange(ev);
        }
      },
      [inputProps?.onChange, onChange]
    );

    useEffect(() => {
      if (!inputRef.current) {
        return;
      }
      if (indeterminate && !checked) {
        inputRef.current.indeterminate = true;
      } else {
        inputRef.current.indeterminate = false;
      }
    }, [indeterminate]);

    return (
      <div data-component={inputName} className={wrapperClasses}>
        <InputLabel
          label={label}
          data-component={labelName}
          size={size}
          error={Boolean(error)}
          className={labelClasses}
          unstyled={unstyled}
        >
          <Input
            inputProps={{ ...inputProps } || {}}
            checked={checked}
            defaultChecked={defaultChecked}
            id={id}
            type="checkbox"
            onChange={localChange}
            name={name}
            required={inputProps?.required || required}
            disabled={inputProps?.disabled || disabled}
            className={inputClasses}
            error={error}
            ref={combinedRef}
            unstyled={unstyled}
          />
        </InputLabel>
      </div>
    );
  }
);
